// 講師派遣申し込みフォームの画面間で共有する定義。
//
// ページテンプレート(.11ty.tsx)とクライアントスクリプト(scripts/*.ts)の
// 両方がここを読む。URL・sessionStorageキー・項目定義を1箇所に集約し、
// 片側だけ直して不整合になるのを防ぐ。
//
// 注意: lectureItems.ts(136KB)をここから import しないこと。
//       scripts/* 経由でクライアントバンドルに載ってしまう。

/** 各画面のURL。遷移先と permalink の両方をここから引く。 */
export const FORM_URL = {
  login: "/lecture/form/login.html",
  input: "/lecture/form/index.html",
  check: "/lecture/form/index/check.html",
  complete: "/lecture/form/index/complete.html",
} as const;

/** 申し込み内容の送信先 API (src/app.ts の apiRoutes と対応) */
export const POST_URL = "/api/lecture/application";

/**
 * eleventy の permalink 形式(先頭スラッシュなし)へ変換する。
 * permalink と遷移先URLを同じ定数から導出し、構造的にズレないようにする。
 */
export const permalinkOf = (url: string): string => url.replace(/^\//, "");

/** sessionStorage のキー */
export const STORAGE_KEY = {
  /** 入力値(入力画面 → 確認画面) */
  data: "lectureFormData",
  /** 送信完了フラグ(確認画面 → 完了画面) */
  complete: "lectureFormComplete",
} as const;

/** 確認画面の「戻る」ボタン。check.ts が id で引く */
export const BACK_BUTTON_ID = "lecture_back_button";

/** 対象者ラジオの value。?item= の audience 部分から引く。 */
export const AUDIENCE_LABEL = {
  worker: "医療従事者",
  resident: "区民向け",
} as const;

/** 対象者ラジオの選択肢(AUDIENCE_LABEL から導出してズレを防ぐ) */
export const AUDIENCE_OPTIONS: string[] = Object.values(AUDIENCE_LABEL);

/** 開催形式ラジオの選択肢 */
export const FORMAT_OPTIONS: string[] = ["現地", "オンライン", "その他"];

/**
 * ?item= の値から対象者ラベルを引く。
 * IDの規則は lectureItems.ts の itemFormId と対になっている
 * (`<診療科slug>-<対象区分><連番2桁>`)。
 */
export function audienceLabelOf(itemId: string): string | undefined {
  const match = /-(worker|resident)\d{2}$/.exec(itemId);
  if (!match) return undefined;
  return AUDIENCE_LABEL[match[1] as keyof typeof AUDIENCE_LABEL];
}

/** フォーム項目。表示順・ラベル・日付欄の唯一の情報源。 */
export interface FormField {
  name: string;
  label: string;
  required?: boolean;
  /** flatpickr を挿す日付欄。確認画面で Y-m-d → Y年m月d日 に整形する対象でもある */
  date?: boolean;
}

/**
 * 項目の定義。フォーム画面の label・確認画面の見出し・
 * バリデーションのエラー文言がすべてここから導出される。
 *
 * 描画に関わる情報(コンポーネントの種類・placeholder・入力幅)は項目ごとに
 * 異なりデータ駆動にすると読みにくくなるため、index.11ty.tsx のJSXに残す。
 */
export const FORM_FIELDS: readonly FormField[] = [
  {
    name: "form_organization",
    label: "申し込み医師会名・団体名",
    required: true,
  },
  { name: "form_applicant_name", label: "申し込み者氏名", required: true },
  { name: "form_tel", label: "電話番号", required: true },
  { name: "form_fax", label: "FAX" },
  { name: "form_email", label: "メールアドレス", required: true },
  { name: "form_lecture_title", label: "希望講座名", required: true },
  { name: "form_purpose", label: "受講目的", required: true },
  { name: "form_audience", label: "対象者", required: true },
  {
    name: "form_preferred_date1",
    label: "希望日時①",
    required: true,
    date: true,
  },
  { name: "form_preferred_date2", label: "希望日時②", date: true },
  { name: "form_preferred_date3", label: "希望日時③", date: true },
  { name: "form_format", label: "開催形式", required: true },
  { name: "form_format_other", label: "開催形式でその他を選んだ方" },
  { name: "form_venue_name", label: "開催地名称", required: true },
  { name: "form_venue_address", label: "開催地住所", required: true },
  { name: "form_participants", label: "参加予定人数", required: true },
  { name: "form_note", label: "その他（自由記載欄）" },
];

const FIELD_BY_NAME: Record<string, FormField> = Object.fromEntries(
  FORM_FIELDS.map((field) => [field.name, field]),
);

/** 項目名からラベルを引く。未知の名前はそのまま返す(呼び出し側を落とさない)。 */
export const labelOf = (name: string): string =>
  FIELD_BY_NAME[name]?.label ?? name;

/** 項目名が必須かどうか */
export const isRequired = (name: string): boolean =>
  FIELD_BY_NAME[name]?.required ?? false;

/** flatpickr を挿す日付欄の項目名 */
export const DATE_FIELDS: string[] = FORM_FIELDS.filter((f) => f.date).map(
  (f) => f.name,
);

export function ready(fn: () => void): void {
  if (document.readyState !== "loading") fn();
  else document.addEventListener("DOMContentLoaded", fn);
}
